import type { ReactNode } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { cn } from '../../lib/utils'

export interface DonutSegment {
	id:    string
	label: string
	value: number
	color?: string  // optional override of segment fill; defaults to monochrome-blue palette
	/**
	 * Цвет точки в легенде. По умолчанию равен `color` (или цвету палитры).
	 * Передаётся отдельно, когда сегменты donut монохромны (один цвет),
	 * а легенда показывает семантические статусы (зелёный/красный/серый).
	 */
	legendColor?: string
}

export interface DonutChartProps {
	segments:    DonutSegment[]
	totalLabel?: string
	totalValue?: ReactNode
	size?:       number  // outer diameter px
	thickness?:  number  // ring thickness px
	className?:  string
	legendValueLabel?: (segment: DonutSegment) => ReactNode
}

// Monochrome blue palette (shadcn-style chart colors). Overridable per segment.
const DEFAULT_PALETTE = ['#2563EB', '#60A5FA', '#BFDBFE', '#E0E7FF', '#F1F5F9']

export function DonutChart({
	segments,
	totalLabel,
	totalValue,
	size = 200,
	thickness = 24,
	className,
	legendValueLabel,
}: DonutChartProps) {
	const total = segments.reduce((acc, s) => acc + s.value, 0)
	const outerRadius = size / 2
	const innerRadius = outerRadius - thickness

	const colored = segments.map((s, i) => {
		const fill = s.color ?? DEFAULT_PALETTE[i % DEFAULT_PALETTE.length] ?? DEFAULT_PALETTE[0]!
		return {
			...s,
			_color:       fill,
			_legendColor: s.legendColor ?? fill,
		}
	})

	return (
		<div className={cn('flex items-center gap-6', className)}>
			<div className="relative shrink-0" style={{ width: size, height: size }}>
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={colored}
							dataKey="value"
							nameKey="label"
							innerRadius={innerRadius}
							outerRadius={outerRadius}
							startAngle={90}
							endAngle={-270}
							stroke="var(--color-background)"
							strokeWidth={2}
						>
							{colored.map((s) => (
								<Cell key={s.id} fill={s._color} />
							))}
						</Pie>
						<Tooltip
							wrapperStyle={{ outline: 'none', zIndex: 10 }}
							content={({ active, payload }) => {
								const item = payload?.[0]
								if (!active || !item || total <= 0) return null
								const fill = (item.payload as { _color?: string })._color
								const pct = Math.round((Number(item.value) / total) * 100)
								return (
									<div className="flex items-center gap-2 rounded-(--radius-md) border border-(--color-border) bg-(--color-background) px-2 py-1 text-sm font-[500] text-(--color-foreground) shadow-(--shadow-md)">
										<span aria-hidden className="size-2.5 rounded-sm" style={{ backgroundColor: fill }} />
										{pct}%
									</div>
								)
							}}
						/>
					</PieChart>
				</ResponsiveContainer>

				<div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
					<span className="text-3xl font-[600] leading-9 tracking-[-0.6px] text-(--color-foreground)">
						{totalValue ?? total}
					</span>
					{totalLabel && (
						<span className="text-xs leading-4 text-(--color-muted-foreground)">{totalLabel}</span>
					)}
				</div>
			</div>

			<ul className="flex flex-1 flex-col gap-2">
				{colored.map((s) => (
					<li key={s.id} className="flex items-center justify-between gap-3 text-sm">
						<span className="flex items-center gap-2 text-(--color-muted-foreground)">
							<span
								aria-hidden
								className="inline-block size-2 shrink-0 rounded-full"
								style={{ backgroundColor: s._legendColor }}
							/>
							{s.label}
						</span>
						<span className="font-[500] text-(--color-foreground)">
							{legendValueLabel ? legendValueLabel(s) : s.value}
						</span>
					</li>
				))}
			</ul>
		</div>
	)
}
